import { OpportunityCompanyView } from '@api-proxies';
import { OpportunityAction } from '@models/enums/OpportunityAction';
import { CustomCellStatusComponent } from '@shared/grids/custom-cell-status/custom-cell-status.component';
import { customCellUserSalesComponent } from '@shared/grids/custom-cell-user-sales/custom-cell-user-sales.component';

import { CustomCellDatepickerComponent } from '@components/shared/grids/custom-cell-datepicker/custom-cell-datepicker.component';
import { TabNames } from '@models/enums/TabNames';
import { CustomCellFavoriteComponent } from '@shared/grids/custom-cell-favorite/custom-cell-favorite.component';
import { CustomHeaderComponent } from '@shared/grids/custom-header/custom-header.component';
import { IRouterNavigationConfig } from '@shared/grids/grid.config';
import { formatDateTimeDisplay } from '@utils/date-utils';
import { currencyFormatter } from '@utils/format-functions';
import { ColDef, ICellRendererParams } from 'ag-grid-enterprise';

export const OPPORTUNITIES_ROUTER_CONFIG: IRouterNavigationConfig = {
    base: '/opportunities-single',
    idKey: 'id',
    extra: [TabNames[TabNames.Overview]]
};

export const OPPORTUNITIES_GRID_COLUMNS: ColDef[] = [
    {
        colId: 'isFavorite',
        field: 'isFavorite',
        headerComponent: CustomHeaderComponent,
        headerComponentParams: { icon: 'star' },
        cellRenderer: CustomCellFavoriteComponent,
        width: 50,
        maxWidth: 50,
        lockPinned: true,
        lockPosition: 'left',
        suppressSizeToFit: true
    },
    {
        colId: 'OID',
        field: 'id',
        headerName: 'OID',
        width: 100,
        lockPinned: true,
        lockPosition: 'left',
        suppressSizeToFit: true,
        cellRenderer: (params: ICellRendererParams) => {
            if (params.value) {
                const link = `/opportunities-single/${params.value}/${TabNames[TabNames.Overview]}`;
                return `<a class="anchor" href="${link}">${params.value}</a>`;
            }
            return '';
        }
    },
    {
        colId: 'name',
        field: 'name',
        headerName: 'Name',
        cellDataType: 'text',
        editable: (params: any) => {
            return availableEdit(params);
        },
        cellEditor: 'agTextCellEditor',
        cellClassRules: {
            'editable-cell': (params: any) => {
                return availableEdit(params);
            }
        }
    },
    {
        colId: 'dueDate',
        field: 'dueDate',
        headerName: 'Due Date',
        cellDataType: 'date',
        editable: (params: any) => {
            return availableEdit(params);
        },
        cellEditor: CustomCellDatepickerComponent,
        cellClassRules: {
            'editable-cell': (params: any) => {
                return availableEdit(params);
            }
        },
        valueGetter: (params) => {
            if (params.data.dueDate) {
                return new Date(params.data.dueDate);
            }
            return null;
        },
        valueFormatter: ({ value }) => formatDateTimeDisplay(value),
        suppressKeyboardEvent: (params) =>
            Boolean(params.editing && (params.event.key === 'Enter' || params.event.key === 'Tab'))
    },
    {
        colId: 'expectedResponseDate',
        field: 'expectedResponseDate',
        headerName: 'Expected Booking Date',
        cellDataType: 'date',
        editable: (params: any) => {
            return availableEdit(params);
        },
        cellEditor: CustomCellDatepickerComponent,
        cellClassRules: {
            'editable-cell': (params: any) => {
                return availableEdit(params);
            }
        },
        valueGetter: (params) => {
            if (params.data.expectedResponseDate) {
                return new Date(params.data.expectedResponseDate);
            }
            return null;
        },
        valueFormatter: ({ value }) => formatDateTimeDisplay(value),
        suppressKeyboardEvent: (params) =>
            Boolean(params.editing && (params.event.key === 'Enter' || params.event.key === 'Tab'))
    },
    {
        colId: 'potentialValue',
        field: 'potentialValue',
        headerName: 'Potential Value ($)',
        valueFormatter: (params: any) => currencyFormatter(params.value),
        editable: (params: any) => {
            return availableEdit(params);
        },
        cellEditor: 'agNumberCellEditor',
        cellClassRules: {
            'editable-cell': (params: any) => {
                return availableEdit(params);
            }
        },
        valueParser: (params: any) => {
            if (params.newValue === '' || params.newValue == null) {
                return null;
            }
            const numValue = parseFloat(params.newValue);
            return isNaN(numValue) ? params.oldValue : numValue;
        }
    },
    {
        colId: 'proposedGrossMarginPercentage',
        field: 'proposedGrossMarginPercentage',
        headerName: 'Potential GM (%)',
        editable: (params: any) => {
            return availableEdit(params);
        },
        cellEditor: 'agNumberCellEditor',
        cellClassRules: {
            'editable-cell': (params: any) => {
                return availableEdit(params);
            }
        },
        cellEditorParams: {
            precision: 2,
            step: 0.1
        },
        valueGetter: (params: any) => {
            const value = params.data?.proposedGrossMarginPercentage;
            return value != null ? value * 100 : null;
        },
        valueFormatter: (params: any) => {
            return params.value != null ? `@${params.value.toFixed(2)}%` : '';
        },
        valueSetter: (params: any) => {
            if (params.newValue === '' || params.newValue == null) {
                params.data.proposedGrossMarginPercentage = null;
                return true;
            }

            const parsed = parseFloat(params.newValue);
            if (isNaN(parsed)) return params.oldValue;

            params.data.proposedGrossMarginPercentage = parsed / 100;
            return true;
        }
    },
    {
        colId: 'probabilityValue',
        field: 'probabilityValue',
        headerName: 'Probability',
        cellEditorPopup: true,
        editable: (params: any) => {
            return availableEdit(params);
        },
        cellEditor: 'agRichSelectCellEditor',
        cellClassRules: {
            'editable-cell': (params: any) => {
                return availableEdit(params);
            }
        },
        valueFormatter: (params) => {
            const cellValue = typeof params.value === 'string' ? parseFloat(params.value) : params.value;

            if (cellValue === null || cellValue === undefined) {
                return '';
            }

            const richSelectOptions = params.colDef.cellEditorParams?.values;
            if (richSelectOptions && Array.isArray(richSelectOptions)) {
                const selectedOption = richSelectOptions.find((option) => option.value === cellValue);
                if (selectedOption && selectedOption.label) {
                    return selectedOption.label;
                }
            }

            if (typeof cellValue === 'number' && !isNaN(cellValue)) {
                return `${(cellValue * 100).toFixed(0)}%`;
            } else if (cellValue?.value) {
                return `${(cellValue.value * 100).toFixed(0)}%`;
            }

            return '';
        }
    },
    {
        colId: 'opportunityStatusName',
        field: 'opportunityStatusName',
        headerName: 'Status',
        cellEditor: 'agRichSelectCellEditor',
        cellEditorPopup: true,
        valueFormatter: (params) => params.value?.label,
        cellRenderer: CustomCellStatusComponent,
        editable: (params: any) => availableEdit(params) && !params.data?._statusLoading,
        cellClassRules: {
            'editable-cell': (params: any) => {
                return availableEdit(params);
            }
        }
    },
    {
        colId: 'salesUsers',
        field: 'salesperson',
        editable: (params: any) => {
            return availableEdit(params);
        },
        cellClassRules: {
            'editable-cell': (params: any) => {
                return availableEdit(params);
            }
        },
        valueFormatter: (params: any) => {
            if (!params.data?.opportunityUsers?.length) {
                return '';
            }
            const allUsers: string[] = [];

            params.data.opportunityUsers.forEach((ou: any) => {
                if (ou.userFullName) {
                    allUsers.push(ou.userFullName.trim());
                }
            });
            return allUsers.join(', ');
        },
        cellEditor: customCellUserSalesComponent
    },
    {
        colId: 'companyNames',
        field: 'companyNames',
        headerName: 'Companies',
        valueFormatter: (params: any) => {
            if (params.data && params.data.opportunityCompanies) {
                return params.data.opportunityCompanies.map((oc: OpportunityCompanyView) => oc.companyName).join(', ');
            }
            return '';
        }
    },
    {
        colId: 'companyRoleNames',
        field: 'companyRoleNames',
        headerName: 'Company Roles',
        valueFormatter: (params: any) => {
            if (!params.data?.opportunityCompanies?.length) {
                return '';
            }

            const uniqueRoles = new Set<string>();

            params.data.opportunityCompanies.forEach((oc: OpportunityCompanyView) => {
                if (oc.companyRoleDescriptions) {
                    const roles = oc.companyRoleDescriptions.split(',').map((role) => role.trim());
                    roles.forEach((role) => uniqueRoles.add(role));
                }
            });

            return Array.from(uniqueRoles).join(', ');
        }
    },
    {
        colId: 'divisionName',
        field: 'divisionName',
        headerName: 'Department',
        cellEditor: 'agRichSelectCellEditor',
        cellEditorPopup: true,
        editable: (params: any) => {
            return availableEdit(params);
        },
        cellClassRules: {
            'editable-cell': (params: any) => {
                return availableEdit(params);
            }
        },
        valueGetter: (params: any) => {
            const data = params.data;
            if (!data) return null;
            const result = getDivisionStateFromData(data);
            if (result.id == null && !result.label) return null;
            return result;
        },
        valueSetter: (params: any) => {
            const { data, newValue } = params;
            if (!data) return false;
            if (typeof newValue === 'string') {
                return false;
            }
            const isClear = newValue == null || (typeof newValue === 'object' && newValue.id == null);
            const current = getDivisionStateFromData(data);
            const incoming = getDivisionStateFromValue(newValue);
            if (divisionStatesEqual(current, incoming)) return false;
            applyDivisionToData(data, newValue, isClear);
            return true;
        },
        valueFormatter: (params: any) => {
            const value = params.value;
            if (!value) return '';
            if (typeof value === 'object' && value.id == null) return '';
            return value.label ?? (typeof value === 'string' ? value : '');
        }
    },
    {
        colId: 'isActive',
        field: 'isActive',
        headerName: 'Condition',
        valueFormatter: (params: any) => {
            return params.value ? 'Active' : 'Archived';
        }
    }
];

function availableEdit(params: any): boolean {
    const isEditable = params.data?.availableActions?.includes(OpportunityAction.Edit);
    return isEditable;
}

function getDivisionStateFromData(data: { divisionId?: any; divisionName?: any }): { id: any; label: string } {
    const id = data.divisionId ?? null;
    const name = data.divisionName;
    const label = !name ? '' : typeof name === 'object' ? (name.label ?? '') : String(name);
    return { id, label };
}

function getDivisionStateFromValue(value: any): { id: any; label: string } {
    const isClear = value == null || (typeof value === 'object' && value.id == null);
    if (isClear) return { id: null, label: '' };
    if (typeof value === 'object') return { id: value.id ?? null, label: value.label ?? '' };
    return { id: null, label: String(value) };
}

function divisionStatesEqual(a: { id: any; label: string }, b: { id: any; label: string }): boolean {
    return a.id === b.id && a.label === b.label;
}

function applyDivisionToData(data: any, value: any, isClear: boolean): void {
    data.divisionId = isClear ? undefined : (value?.id ?? undefined);
    data.divisionName = isClear ? null : value;
}
